import type { InsertNode } from "./node";

export type UnitTypeValue = "measurement" | "currency" | "percentage" | "information";

// Display/suggestion order for unit categories. Preserves the original units.json key order.
export const UNIT_TYPE_VALUES = [
  "percentage",
  "measurement",
  "currency",
  "information",
] as const satisfies readonly UnitTypeValue[];

export type UnitNodePlacement = "prefix" | "postfix";

/** Custom SymPy Quantity definition for units absent from sympy.physics.units (e.g. klf, ksi). */
export type CustomSympyUnit = {
  /** Local Python symbol (assigned onto the `units` module) and display round-trip key, e.g. "klf". */
  name: string;
  /** sympy.physics.units dimension expression, e.g. "force / length". */
  dimension: string;
  /** Scale-factor Python expr in terms of `units.*` and prior custom names, e.g. "units.kip / units.foot". */
  scaleFactor: string;
};

export type Unit = {
  category: UnitTypeValue | UnitTypeValue[];
  placement: UnitNodePlacement;
  children: InsertNode[];
  name?: string;
  keywords?: string[];
  /** SymPy physics.units name (e.g. "kilogram"). Absent = non-SI unit. */
  sympyName?: string;
  /** Custom Quantity definition, for units not in sympy.physics.units. Mutually exclusive with sympyName. */
  customSympy?: CustomSympyUnit;
  /** sympy dimension expression (e.g. "length", "force / length"). Present iff sympyName or customSympy is. Same-dimension units are mutually convertible via convert_to. */
  dimension?: string;
};

export function unitCategories(unit: Unit): UnitTypeValue[] {
  return Array.isArray(unit.category) ? unit.category : [unit.category];
}

export type UnitWithType = Unit & { unitType?: UnitTypeValue };

export type UnitNodeProps<UT extends UnitTypeValue> = {
  unitType?: UT;
  name?: string;
  placement?: UnitNodePlacement;
};

export type UnitNodeConstructorProps<UT extends UnitTypeValue = UnitTypeValue> = UnitNodeProps<UT>;
